/**
 * Employer (seat) cancellation on web: the pre-redesign single-sheet review,
 * kept for `planSet: 'employer'`. Individual plans use the multi-step
 * subscription journey in `CancelPlanDialog.web.tsx`.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscriptionState } from '../../hooks/useSubscriptionState';
import { useSubscriptionDisplay } from '../../lib/subscription-display';
import { useCancelPlanFlow } from '../../hooks/useCancelPlanFlow';
import { useLocale } from '../../hooks/useLocale';
import {
  CANCEL_REASONS,
  formatCancelAccessDate,
  getCancelAccessDate,
  getCancelDestination,
  type CancelReason,
} from '../../lib/cancel-retention';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { WebDialog } from '../common/WebDialog.web';
import { SubscriptionPlanBadge } from './SubscriptionPlanBadge';
import { SubscriptionValue } from './SubscriptionValue';

type Props = {
  open: boolean;
  onClose: () => void;
  onContinueToStore: () => Promise<void>;
};

export function EmployerCancelPlanDialog(props: Props) {
  return props.open ? <CancelSession {...props} /> : null;
}

function CancelSession({ onClose, onContinueToStore }: Props) {
  const { t } = useTranslation('common');
  const { locale, isRTL } = useLocale();
  const { catalog, snapshot } = useSubscriptionState();
  const display = useSubscriptionDisplay('web');
  const flow = useCancelPlanFlow({
    surface: 'web',
    snapshot,
    onClose,
    onContinueToStore,
  });
  const date = formatCancelAccessDate(getCancelAccessDate(snapshot), locale);
  const destination = getCancelDestination(snapshot?.store);
  const plan = display.tierLabel;
  const employer = snapshot?.planSet === 'employer';
  const access =
    snapshot?.status === 'trialing'
      ? t('settings.cancelFlow.trialAccess')
      : date
        ? t('settings.cancelFlow.accessUntil', { date })
        : t('settings.cancelFlow.accessUnknown');
  const responseKey =
    employer &&
    (flow.reason === 'not_needed_now' || flow.reason === 'hard_to_use')
      ? 'employerResponse'
      : `response.${flow.reason}`;
  return (
    <WebDialog
      title={t('settings.cancelFlow.sheetTitle')}
      onClose={() => flow.close()}
      maxWidth={500}
      hideHeader
      zIndex={1100}
    >
      <ReviewFocus onClose={() => flow.close()}>
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            maxHeight: 'min(800px, calc(100dvh - 64px))',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 24px 8px',
              flexShrink: 0,
            }}
          >
            <SubscriptionPlanBadge label={plan} />
            <button
              type="button"
              aria-label={t('actions.close')}
              aria-disabled={flow.isRedirecting}
              onClick={() => flow.close()}
              style={closeStyle}
            >
              <MaterialSymbol name="close" size={22} />
            </button>
          </div>
          <div
            style={{
              padding: '12px 24px 24px',
              overflowY: 'auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 28,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}
              >
                {t('settings.cancelFlow.title', { plan })}
              </h2>
              <p style={{ ...bodyStyle, marginTop: 8 }}>
                {t('settings.cancelFlow.body')}
              </p>
            </div>
            <SubscriptionValue
              catalog={catalog}
              snapshot={snapshot}
              planLabel={plan}
            />
            <div>
              <label
                htmlFor="cancel-reason"
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 8,
                }}
              >
                {t('settings.cancelFlow.reasonTitle')}{' '}
                <span
                  style={{ color: 'var(--text-secondary)', fontWeight: 400 }}
                >
                  · {t('settings.cancelFlow.reasonOptional')}
                </span>
              </label>
              <select
                id="cancel-reason"
                disabled={flow.isRedirecting}
                value={flow.reason ?? ''}
                onChange={(e) =>
                  flow.selectReason(
                    (e.target.value || null) as CancelReason | null,
                  )
                }
                style={{
                  width: '100%',
                  minHeight: 44,
                  padding: '10px 14px',
                  borderRadius: 16,
                  border: '1px solid var(--input-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)',
                  font: 'inherit',
                  fontSize: 13,
                }}
              >
                <option value="">{t('settings.cancelFlow.noReason')}</option>
                {CANCEL_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {t(`settings.cancelFlow.reasons.${reason}`)}
                  </option>
                ))}
              </select>
              {flow.reason ? (
                <p aria-live="polite" style={{ ...bodyStyle, marginTop: 10 }}>
                  {t(`settings.cancelFlow.${responseKey}`)}
                </p>
              ) : null}
            </div>
            <div
              style={{
                paddingTop: 18,
                borderTop: '1px solid var(--input-border)',
              }}
            >
              <p style={{ ...bodyStyle, color: 'var(--text-primary)' }}>
                {access}
              </p>
              <p style={{ ...bodyStyle, marginTop: 5 }}>
                {t(
                  `settings.cancelFlow.${employer ? 'employerAfter' : 'freeAfter'}`,
                )}
              </p>
            </div>
          </div>
          <div
            style={{
              padding: '14px 24px max(20px, env(safe-area-inset-bottom))',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              borderTop: '1px solid var(--input-border)',
              flexShrink: 0,
            }}
          >
            {flow.handoffFailed ? (
              <p
                role="alert"
                style={{ ...bodyStyle, color: 'var(--text-primary)' }}
              >
                {t('settings.cancelFlow.handoffError')}
              </p>
            ) : null}
            <button
              type="button"
              disabled={flow.isRedirecting}
              onClick={() => flow.close('keep_plan_selected')}
              style={{
                ...buttonStyle,
                background: 'var(--text-primary)',
                color: 'var(--bg)',
                border: '1px solid transparent',
              }}
            >
              {t('settings.cancelFlow.keepPlan', { plan })}
            </button>
            <button
              type="button"
              disabled={flow.isRedirecting}
              onClick={() => void flow.continueToStore()}
              style={{
                ...buttonStyle,
                background: 'transparent',
                color: 'var(--text-primary)',
                border: '1px solid var(--input-border)',
              }}
            >
              {flow.isRedirecting
                ? t('settings.cancelFlow.opening')
                : t(`settings.cancelFlow.continueCta.${destination}`)}
            </button>
            <p style={{ ...bodyStyle, fontSize: 11, textAlign: 'center' }}>
              {t(`settings.cancelFlow.providerNote.${destination}`)}
            </p>
          </div>
        </div>
      </ReviewFocus>
    </WebDialog>
  );
}

// This review sits above Settings. Keep keyboard focus in the top dialog and
// restore it to the cancel button, including when a nested portal is dismissed.
function ReviewFocus({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const container = root.current!;
    const previous = document.activeElement as HTMLElement | null;
    const overlay = container.closest('[role="dialog"]')?.parentElement;
    const background = Array.from(document.body.children).filter(
      (node): node is HTMLElement =>
        node instanceof HTMLElement &&
        node !== overlay &&
        !node.contains(container),
    );
    const priorInert = background.map((node) => node.inert);
    background.forEach((node) => {
      node.inert = true;
    });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const controls = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          'button:not(:disabled), select:not(:disabled), [tabindex="0"]',
        ),
      ).filter((node) => node.getClientRects().length > 0);
    const focusFirst = () => (controls()[0] ?? container).focus();
    focusFirst();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close.current();
      }
      if (event.key !== 'Tab') return;
      const nodes = controls();
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first) {
        event.preventDefault();
        container.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onFocus = (event: FocusEvent) => {
      if (!container.contains(event.target as Node)) focusFirst();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocus);
      background.forEach((node, index) => {
        node.inert = priorInert[index];
      });
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return (
    <div
      ref={root}
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}
const bodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--text-secondary)',
};
const buttonStyle: CSSProperties = {
  width: '100%',
  minHeight: 46,
  padding: '12px 16px',
  borderRadius: 999,
  font: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};
const closeStyle: CSSProperties = {
  width: 44,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  borderRadius: '50%',
  background: 'var(--hover)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
};
